import { DailyBriefing } from "@/components/ai/DailyBriefing";
import { GettingStarted } from "@/components/engage/GettingStarted";
import { HomeHero } from "@/components/home/HomeHero";
import { HotGames } from "@/components/home/HotGames";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, TrendingDown, TrendingUp } from "lucide-react";
import { API, useAuth } from "@/lib/auth";
import { ErrorBox, Freshness, InsightCard, PriceChart, Skel, compact, money, pctTxt } from "@/components/market/Shared";

const Change = ({ v, testid }) => (
  <span className={`inline-flex items-center gap-1 font-num text-sm ${v >= 0 ? "text-emerald-300" : "text-red-300"}`} data-testid={testid}>
    {v >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{pctTxt(v)}
  </span>
);

export default function Home() {
  const { profile } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState("bitcoin");
  const [mk, setMk] = useState(null);
  const load = useCallback(() => axios.get(`${API}/crypto/prices`).then((r) => { setD(r.data); setErr(null); })
    .catch(() => setErr("Live prices are unavailable right now.")), []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  useEffect(() => { axios.get(`${API}/crypto/markets`, { params: { limit: 250 } }).then((r) => setMk(r.data)).catch(() => {}); }, []);
  const a = d?.assets.find((x) => x.id === sel);
  const byId = Object.fromEntries((mk?.assets || []).map((x) => [x.id, x]));
  const watch = (profile?.watchlist || []).map((id) => byId[id]).filter(Boolean);
  const recent = (profile?.recent || []).map((id) => byId[id]).filter(Boolean).slice(0, 6);
  const total = mk ? mk.assets.reduce((s, x) => s + (x.market_cap || 0), 0) : null;
  const ups = mk ? mk.assets.filter((x) => x.change_24h_pct > 0).length : null;

  return (
    <div className="space-y-5" data-testid="home-page">
      <HomeHero />
      <GettingStarted />
      <HotGames />
      <h2 className="font-head font-extrabold uppercase text-2xl pt-2">Crypto right now</h2>
      {err && <ErrorBox msg={err} onRetry={load} />}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {(d?.assets || [null, null, null]).map((x, i) => x ? (
          <button key={x.id} onClick={() => setSel(x.id)} data-testid={`core-${x.symbol}`}
            className={`glass p-3 sm:p-5 text-left transition-transform hover:-translate-y-0.5 ${sel === x.id ? "ring-1 ring-[var(--cyan)]" : ""}`}>
            <div className="text-xs text-slate-400 font-semibold">{x.symbol}<span className="hidden sm:inline"> · {x.name}</span></div>
            <div className="font-num font-semibold text-sm sm:text-2xl mt-1 truncate" data-testid={`core-${x.symbol}-price`}>{money(x.price, x.price > 1000 ? 0 : 2)}</div>
            <Change v={x.change_24h_pct} testid={`core-${x.symbol}-change`} />
          </button>
        ) : <Skel key={i} />)}
      </div>
      {d && <Freshness d={d} testid="prices-freshness" />}
      {a && (
        <section className="glass p-4 sm:p-6" data-testid="home-chart-card">
          <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
            <div>
              <div className="text-sm text-slate-400">{a.name}</div>
              <div className="font-num text-3xl font-semibold">{money(a.price)}</div>
            </div>
            <div className="text-xs text-slate-400 font-num text-right space-y-0.5">
              <div>24h high {money(a.high_24h)} · low {money(a.low_24h)}</div>
              <div data-testid="home-volume">24h volume {compact(a.volume_24h)}</div>
            </div>
          </div>
          <PriceChart asset={a.id} />
          <Link to={`/asset/${a.id}`} className="inline-block mt-3 text-sm text-[var(--cyan)]" data-testid="home-open-asset">Open {a.symbol} details →</Link>
        </section>
      )}
      <InsightCard asset={sel} signedIn={!!profile} />
      <DailyBriefing />
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="glass p-5" data-testid="market-summary">
          <h3 className="font-head font-semibold">Market summary</h3>
          {mk ? <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
            <div><div className="text-slate-500 text-xs">Top-250 market cap</div><div className="font-num text-lg">{compact(total)}</div></div>
            <div><div className="text-slate-500 text-xs">Up in 24h</div><div className="font-num text-lg">{ups} / {mk.assets.length}</div></div>
            <div className="col-span-2"><Freshness d={mk} testid="summary-freshness" /></div>
          </div> : <Skel h="h-20" />}
        </section>
        <section className="glass p-5" data-testid="home-watchlist">
          <h3 className="font-head font-semibold flex items-center gap-2"><Star className="w-4 h-4 text-[var(--violet)]" />Watchlist</h3>
          {!profile ? <p className="text-sm text-slate-400 mt-2"><Link className="text-[var(--cyan)]" to="/account">Sign in</Link> to save coins to a watchlist.</p> :
            watch.length ? <div className="mt-2 divide-y divide-[var(--line)]">{watch.map((x) => (
              <Link key={x.id} to={`/asset/${x.id}`} className="flex items-center justify-between py-2 text-sm" data-testid={`watch-${x.id}`}>
                <span className="flex items-center gap-2"><img src={x.image} alt="" className="w-5 h-5 rounded-full" />{x.name}</span>
                <span className="font-num">{money(x.price)} <Change v={x.change_24h_pct} /></span>
              </Link>))}</div> : <p className="text-sm text-slate-400 mt-2">Empty. Tap the star on any coin in <Link className="text-[var(--cyan)]" to="/markets">Markets</Link>.</p>}
          {recent.length > 0 && <div className="mt-4" data-testid="home-recent">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Recently viewed</div>
            <div className="flex flex-wrap gap-2 mt-2">{recent.map((x) => <Link key={x.id} to={`/asset/${x.id}`} className="px-3 py-1 rounded-full border border-[var(--line)] text-xs">{x.symbol}</Link>)}</div>
          </div>}
        </section>
      </div>
    </div>
  );
}
